<template>
  <Layout>
    <PageHeader :title="$t('menuitems.users.list.activateDevice')" :items="breadcrumbItems" />

    <div class="row">
      <div class="col-12 col-xl-5">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title mb-3">Autorizar novo dispositivo</h5>
            <p class="text-muted">
              Digite o código exibido na TV e informe um nome para identificar o dispositivo.
            </p>

            <div class="mb-3">
              <label class="form-label">Código</label>
              <input
                v-model="form.code"
                type="text"
                class="form-control"
                placeholder="A8X2-9PZ1"
                maxlength="9"
                @input="normalizeCode"
              />
            </div>

            <div class="mb-3">
              <label class="form-label">Nome do dispositivo</label>
              <input
                v-model="form.name"
                type="text"
                class="form-control"
                placeholder="TV Recepção"
                maxlength="120"
              />
            </div>

            <button class="btn btn-primary" :disabled="submitting" @click="authorizeDevice">
              <span
                v-if="submitting"
                class="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              />
              Autorizar dispositivo
            </button>
          </div>
        </div>
      </div>

      <div class="col-12 col-xl-7">
        <div class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h5 class="card-title mb-0">Dispositivos autorizados</h5>
              <button class="btn btn-outline-secondary btn-sm" :disabled="loading" @click="loadDevices">
                Atualizar
              </button>
            </div>

            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading</span>
              </div>
            </div>

            <div v-else-if="devices.length === 0" class="alert alert-info mb-0">
              Nenhum dispositivo autorizado para esta empresa.
            </div>

            <div v-else class="table-responsive">
              <table class="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Status</th>
                    <th>Última atividade</th>
                    <th class="text-end">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="device in devices" :key="device.id">
                    <td>{{ device.name || 'Sem nome' }}</td>
                    <td>
                      <span
                        class="badge"
                        :class="device.status === 'active' ? 'bg-success' : 'bg-secondary'"
                      >
                        {{ device.status === 'active' ? 'Ativa' : 'Inativa' }}
                      </span>
                    </td>
                    <td>{{ formatDate(device.last_seen_at || device.created_at) }}</td>
                    <td class="text-end">
                      <button
                        class="btn btn-sm btn-outline-danger"
                        :disabled="revokingId === device.id"
                        @click="revokeDevice(device)"
                      >
                        Revogar
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from '@/views/layouts/main';
import PageHeader from '@/components/page-header';
import TvDevicesController from '@/controller/TvDevicesController';
import Swal from 'sweetalert2';

export default {
  name: 'ActivateDevice',
  components: { Layout, PageHeader },
  data() {
    return {
      breadcrumbItems: [
        { text: this.$t('menuitems.users.text'), href: '#' },
        { text: this.$t('menuitems.users.list.activateDevice'), active: true },
      ],
      devices: [],
      loading: false,
      submitting: false,
      revokingId: null,
      form: {
        code: '',
        name: '',
      },
    };
  },
  created() {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const empresa = JSON.parse(localStorage.getItem('currentEmpresa') || '{}');
    this.controller = new TvDevicesController(user, empresa);
    this.loadDevices();
  },
  methods: {
    showAlert(icon, title, text = '') {
      return Swal.fire({
        icon,
        title,
        text,
      });
    },
    normalizeCode() {
      this.form.code = (this.form.code || '').toUpperCase().replace(/[^A-Z0-9-]/g, '');
    },
    formatDate(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '-';
      return date.toLocaleString('pt-BR');
    },
    async loadDevices() {
      this.loading = true;
      try {
        const response = await this.controller.list();
        this.devices = response.data || [];
      } catch (error) {
        this.showAlert('error', 'Erro', 'Não foi possível carregar os dispositivos.');
      } finally {
        this.loading = false;
      }
    },
    async authorizeDevice() {
      if (!this.form.code || !this.form.name) {
        this.showAlert('warning', 'Atenção', 'Informe código e nome do dispositivo.');
        return;
      }

      this.submitting = true;
      try {
        await this.controller.authorize(this.form.code, this.form.name);
        this.showAlert('success', 'Sucesso', 'Dispositivo autorizado com sucesso.');
        this.form.code = '';
        this.form.name = '';
        await this.loadDevices();
      } catch (error) {
        this.showAlert('error', 'Erro', 'Falha ao autorizar dispositivo.');
      } finally {
        this.submitting = false;
      }
    },
    async revokeDevice(device) {
      this.revokingId = device.id;
      try {
        await this.controller.revoke(device.id);
        this.showAlert('success', 'Sucesso', 'Dispositivo revogado com sucesso.');
        await this.loadDevices();
      } catch (error) {
        this.showAlert('error', 'Erro', 'Falha ao revogar dispositivo.');
      } finally {
        this.revokingId = null;
      }
    },
  },
};
</script>
